'use client';
import { useSyncExternalStore } from 'react';
import { SLIDE_COUNT, slides, transitionSeconds } from './slides';

/**
 * Presentation state: a tiny external store shared by the DOM overlay (React, via
 * useSyncExternalStore) and the 3D world (read directly inside the render loop, so camera
 * and scene animation never trigger React renders).
 */

export type PresentationMode =
  /** The stage owns the viewport; wheel/touch/keys move between slides. */
  | 'presenting'
  /** After the last slide the page scrolls normally (FAQ, footer). */
  | 'released';

export interface PresentationState {
  index: number;
  previous: number;
  direction: 1 | -1;
  /** performance.now() when the current slide was requested. */
  enteredAt: number;
  /** Seconds the camera transition into the current slide takes. */
  transition: number;
  /** performance.now() when the current slide's story starts (transition end). */
  storyStartAt: number;
  transitioning: boolean;
  mode: PresentationMode;
  /** Ecosystem node under the pointer / keyboard focus. */
  focus: string | null;
  reducedMotion: boolean;
  compact: boolean;
  /** Set once the 3D world has drawn its first frames. */
  worldReady: boolean;
}

type Listener = () => void;

let state: PresentationState = {
  index: 0,
  previous: 0,
  direction: 1,
  enteredAt: 0,
  transition: 0,
  storyStartAt: 0,
  transitioning: false,
  mode: 'presenting',
  focus: null,
  reducedMotion: false,
  compact: false,
  worldReady: false,
};

const listeners = new Set<Listener>();
let transitionTimer: ReturnType<typeof setTimeout> | undefined;

export const presentation = {
  get: () => state,
  set(partial: Partial<PresentationState>) {
    state = { ...state, ...partial };
    listeners.forEach((l) => l());
  },
  subscribe(listener: Listener) {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  },
};

/** Selector hook. Selectors must return primitives (or stable references). */
export function usePresentation<T>(selector: (s: PresentationState) => T): T {
  return useSyncExternalStore(
    presentation.subscribe,
    () => selector(state),
    () => selector(state)
  );
}

/** Moves to `target`. Returns false when already there. `instant` skips the camera journey. */
export function goTo(target: number, { instant = false } = {}) {
  const i = Math.max(0, Math.min(SLIDE_COUNT - 1, target));
  if (i === state.index) return false;
  const now = performance.now();
  const seconds = instant
    ? 0
    : transitionSeconds(state.index, i, {
        reducedMotion: state.reducedMotion,
        compact: state.compact,
      });
  clearTimeout(transitionTimer);
  presentation.set({
    previous: state.index,
    index: i,
    direction: i > state.index ? 1 : -1,
    enteredAt: now,
    transition: seconds,
    storyStartAt: now + seconds * 1000,
    transitioning: seconds > 0,
    focus: null,
  });
  if (seconds > 0) {
    transitionTimer = setTimeout(() => presentation.set({ transitioning: false }), seconds * 1000);
  }
  return true;
}

export const next = () => goTo(state.index + 1);
export const prev = () => goTo(state.index - 1);

/** Seconds of story time on the current slide (negative while the camera is travelling). */
export function storyTime(now = performance.now()) {
  return (now - state.storyStartAt) / 1000;
}

export function currentSlide() {
  return slides[state.index];
}
